"use client";
// 26차 후속 — 운영대시보드 위젯 1차 보정: 소싱 업사이드 상품 (공유 SoT).
// 모든 직원 조회/추가/수정 가능. 삭제는 soft delete (is_active=false).
// 1차 필드: planned_product_name / purchase_url / benchmark_url / expected_cost_krw.

import { useEffect, useState } from "react";
import {
  listSourcingUpside,
  createSourcingUpside,
  updateSourcingUpside,
  softDeleteSourcingUpside,
  type SourcingUpsideItem,
} from "@/lib/api";

function isOptionalHttpUrl(v: string): boolean {
  const s = v.trim();
  if (!s) return true; // 빈 값 허용
  const low = s.toLowerCase();
  return low.startsWith("http://") || low.startsWith("https://");
}

function fmtKRW(n: number | null): string {
  if (n === null || n === undefined || Number.isNaN(n)) return "-";
  return Math.round(n).toLocaleString("ko-KR") + "원";
}

interface FormState {
  planned_product_name: string;
  purchase_url: string;
  benchmark_url: string;
  expected_cost_krw: string;
}

const emptyForm: FormState = {
  planned_product_name: "",
  purchase_url: "",
  benchmark_url: "",
  expected_cost_krw: "",
};

export default function SourcingUpsideCard() {
  const [rows, setRows] = useState<SourcingUpsideItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [adding, setAdding] = useState(false);
  const [newForm, setNewForm] = useState<FormState>(emptyForm);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<FormState>(emptyForm);

  async function reload() {
    setLoading(true);
    try {
      const res = await listSourcingUpside(false);
      setRows(res);
      setError(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "조회 실패");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
  }, []);

  function validateForm(f: FormState): string | null {
    if (!f.planned_product_name.trim()) return "예정 상품명을 입력하세요.";
    if (!isOptionalHttpUrl(f.purchase_url))
      return "구매링크 URL 은 http:// 또는 https:// 로 시작해야 합니다.";
    if (!isOptionalHttpUrl(f.benchmark_url))
      return "벤치마킹링크 URL 은 http:// 또는 https:// 로 시작해야 합니다.";
    if (f.expected_cost_krw.trim()) {
      const n = Number(f.expected_cost_krw.replace(/[,\s]/g, ""));
      if (!Number.isFinite(n) || n < 0) return "예상원가는 0 이상의 숫자여야 합니다.";
    }
    return null;
  }

  function formToPayload(f: FormState) {
    const cost = f.expected_cost_krw.trim()
      ? Number(f.expected_cost_krw.replace(/[,\s]/g, ""))
      : null;
    return {
      planned_product_name: f.planned_product_name.trim(),
      purchase_url: f.purchase_url.trim() || null,
      benchmark_url: f.benchmark_url.trim() || null,
      expected_cost_krw: cost,
    };
  }

  async function handleCreate() {
    const msg = validateForm(newForm);
    if (msg) {
      alert(msg);
      return;
    }
    try {
      await createSourcingUpside(formToPayload(newForm));
      setNewForm(emptyForm);
      setAdding(false);
      await reload();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : "추가 실패");
    }
  }

  function startEdit(row: SourcingUpsideItem) {
    setEditingId(row.id);
    setEditForm({
      planned_product_name: row.planned_product_name,
      purchase_url: row.purchase_url || "",
      benchmark_url: row.benchmark_url || "",
      expected_cost_krw:
        row.expected_cost_krw === null || row.expected_cost_krw === undefined
          ? ""
          : String(row.expected_cost_krw),
    });
  }

  async function handleSaveEdit(id: number) {
    const msg = validateForm(editForm);
    if (msg) {
      alert(msg);
      return;
    }
    try {
      await updateSourcingUpside(id, formToPayload(editForm));
      setEditingId(null);
      await reload();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : "수정 실패");
    }
  }

  async function handleSoftDelete(id: number) {
    if (!window.confirm("이 후보를 비활성 처리할까요? (소프트 삭제)")) return;
    try {
      await softDeleteSourcingUpside(id);
      await reload();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : "비활성 실패");
    }
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-bold text-gray-700">📈 소싱 업사이드 상품</p>
        <span className="text-[10px] text-gray-400">공유 · soft delete</span>
      </div>

      {loading && <p className="text-xs text-gray-400">불러오는 중...</p>}
      {error && <p className="text-xs text-red-600">⚠️ {error}</p>}

      {!loading && rows.length === 0 && (
        <p className="text-xs text-gray-400 py-1">
          등록된 후보가 없습니다. 아래 "+ 후보 추가" 로 등록하세요.
        </p>
      )}

      {!loading && rows.length > 0 && (
        <ul className="space-y-2 max-h-72 overflow-y-auto">
          {rows.map((r) => (
            <li
              key={r.id}
              data-testid="sourcing-upside-row"
              className="border border-gray-100 rounded p-2 text-xs"
            >
              {editingId === r.id ? (
                <div className="space-y-1">
                  <input
                    type="text"
                    value={editForm.planned_product_name}
                    onChange={(e) =>
                      setEditForm({ ...editForm, planned_product_name: e.target.value })
                    }
                    placeholder="예정 상품명"
                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                  />
                  <input
                    type="url"
                    value={editForm.purchase_url}
                    onChange={(e) =>
                      setEditForm({ ...editForm, purchase_url: e.target.value })
                    }
                    placeholder="구매링크 https://..."
                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                  />
                  <input
                    type="url"
                    value={editForm.benchmark_url}
                    onChange={(e) =>
                      setEditForm({ ...editForm, benchmark_url: e.target.value })
                    }
                    placeholder="벤치마킹링크 https://..."
                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                  />
                  <input
                    type="number"
                    min={0}
                    value={editForm.expected_cost_krw}
                    onChange={(e) =>
                      setEditForm({ ...editForm, expected_cost_krw: e.target.value })
                    }
                    placeholder="예상원가(원)"
                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                  />
                  <div className="flex gap-1 justify-end">
                    <button
                      type="button"
                      onClick={() => handleSaveEdit(r.id)}
                      className="px-2 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded"
                    >
                      저장
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingId(null)}
                      className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded"
                    >
                      취소
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-gray-800 truncate" title={r.planned_product_name}>
                        {r.planned_product_name}
                      </p>
                      <p className="text-[11px] text-gray-500">
                        예상원가 {fmtKRW(r.expected_cost_krw)}
                      </p>
                      <div className="flex gap-2 mt-1 flex-wrap">
                        {r.purchase_url && (
                          <a
                            href={r.purchase_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[11px] text-blue-600 underline truncate max-w-[140px]"
                            title={r.purchase_url}
                          >
                            구매링크 ↗
                          </a>
                        )}
                        {r.benchmark_url && (
                          <a
                            href={r.benchmark_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[11px] text-purple-600 underline truncate max-w-[140px]"
                            title={r.benchmark_url}
                          >
                            벤치마킹 ↗
                          </a>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1 shrink-0">
                      <button
                        type="button"
                        onClick={() => startEdit(r)}
                        className="px-2 py-0.5 text-[11px] text-gray-500 hover:text-gray-800 border border-gray-200 rounded"
                      >
                        수정
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSoftDelete(r.id)}
                        className="px-2 py-0.5 text-[11px] text-red-500 hover:text-red-700 border border-gray-200 rounded"
                      >
                        비활성
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {adding ? (
        <div className="mt-2 space-y-1">
          <input
            type="text"
            value={newForm.planned_product_name}
            onChange={(e) => setNewForm({ ...newForm, planned_product_name: e.target.value })}
            placeholder="예정 상품명"
            className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
          />
          <input
            type="url"
            value={newForm.purchase_url}
            onChange={(e) => setNewForm({ ...newForm, purchase_url: e.target.value })}
            placeholder="구매링크 https://..."
            className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
          />
          <input
            type="url"
            value={newForm.benchmark_url}
            onChange={(e) => setNewForm({ ...newForm, benchmark_url: e.target.value })}
            placeholder="벤치마킹링크 https://..."
            className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
          />
          <input
            type="number"
            min={0}
            value={newForm.expected_cost_krw}
            onChange={(e) => setNewForm({ ...newForm, expected_cost_krw: e.target.value })}
            placeholder="예상원가(원)"
            className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
          />
          <div className="flex gap-1 justify-end">
            <button
              type="button"
              onClick={handleCreate}
              className="px-2 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded"
            >
              추가
            </button>
            <button
              type="button"
              onClick={() => {
                setAdding(false);
                setNewForm(emptyForm);
              }}
              className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded"
            >
              취소
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="mt-2 w-full text-xs px-2 py-1 border border-dashed border-gray-300 text-gray-500 rounded hover:bg-gray-50"
        >
          + 후보 추가
        </button>
      )}
    </div>
  );
}
